import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/popover";
import { useExperimentTags, useSetExperimentTags } from "@/http/api-hooks";
import { Experiment, ExperimentTag } from "@/http/api-types";
import { cn } from "@/lib/utils";
import { Loader2, Plus, Tag as TagIcon, X } from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  addTagName,
  canCreateTag,
  isTagSelected,
  normalizeTagName,
  removeTagName,
  tagSearchDebounceMs,
  tagSearchMinLength,
  tagSearchTerm,
  tagSuggestions,
} from "./experiment-tags";

/** Path to the experiments list filtered down to a single tag. */
export function experimentTagFilterPath(name: string): string {
  return `/experiments?tag=${encodeURIComponent(name)}`;
}

type ExperimentTagBadgeProps = {
  name: string;
  onRemove?: () => void;
  removeDisabled?: boolean;
  /** Extra classes for the remove button, so a table cell can reveal it on row hover. */
  removeClassName?: string;
};

/**
 * A tag badge. The label always navigates to the experiments list filtered by that tag,
 * which is how a tag turns into a way to find related experiments.
 */
export function ExperimentTagBadge({ name, onRemove, removeDisabled, removeClassName }: ExperimentTagBadgeProps) {
  const navigate = useNavigate();

  return (
    <Badge variant="secondary" className={cn("gap-1 font-medium", onRemove ? "pr-1" : undefined)}>
      <button
        type="button"
        className="max-w-[16rem] truncate hover:underline"
        onClick={() => navigate(experimentTagFilterPath(name))}
        title={`Show experiments tagged "${name}"`}
      >
        {name}
      </button>
      {onRemove && (
        <button
          type="button"
          className={cn(
            "rounded-sm p-0.5 text-muted-foreground hover:bg-background/60 hover:text-foreground disabled:opacity-50",
            removeClassName,
          )}
          onClick={onRemove}
          disabled={removeDisabled}
          aria-label={`Remove tag ${name}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </Badge>
  );
}

/**
 * Add and remove tags on one experiment, saving the whole list on every change.
 *
 * `onTagsSaved` is only needed where the caller holds its own copy of the experiment (the
 * detail page). The list page needs nothing: the mutation writes the new tags straight
 * into the cached list rows, so the table re-renders on its own.
 */
function useExperimentTagEditing(
  experimentId: number,
  tags: ExperimentTag[] | undefined,
  onTagsSaved?: (tags: ExperimentTag[]) => void,
) {
  const { trigger, isMutating } = useSetExperimentTags(experimentId);
  const selectedNames = useMemo(() => (tags || []).map(tag => tag.name), [tags]);

  const saveTags = async (names: string[]) => {
    try {
      const response = await trigger(names);
      onTagsSaved?.(response.data);
    } catch (error) {
      console.error("Failed to update experiment tags:", error);
      toast.error("Failed to update tags. Please try again.");
    }
  };

  return {
    selectedNames,
    isMutating,
    addTag: (name: string) => {
      if (isTagSelected(selectedNames, name)) {
        return;
      }
      void saveTags(addTagName(selectedNames, name));
    },
    removeTag: (name: string) => void saveTags(removeTagName(selectedNames, name)),
  };
}

type ExperimentTagPickerProps = {
  selectedNames: string[];
  onSelect: (name: string) => void;
  /** The popover trigger. Rendered with `asChild`, so it must accept a ref. */
  children: ReactNode;
};

/**
 * The tag search-and-create popover, shared by the detail page editor and the list cell.
 * Owns the debounce so no caller has to repeat it.
 */
export function ExperimentTagPicker({ selectedNames, onSelect, children }: ExperimentTagPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");

  // Hold the query back until the input settles, so a typed word costs one request
  // instead of one per character.
  useEffect(() => {
    const nextSearch = tagSearchTerm(inputValue);
    if (nextSearch === search) {
      return;
    }

    const timeout = window.setTimeout(() => setSearch(nextSearch), tagSearchDebounceMs);
    return () => window.clearTimeout(timeout);
  }, [inputValue, search]);

  const { data: tagOptions, isLoading, isValidating } = useExperimentTags(search, isOpen);

  const suggestions = useMemo(
    () => tagSuggestions(tagOptions, selectedNames, search),
    [search, selectedNames, tagOptions],
  );
  // The visible suggestions only answer the current input once the debounce has fired and
  // the request has landed; until then a "Create" row could contradict them.
  const suggestionsMatchInput = search === tagSearchTerm(inputValue) && !isValidating;
  const showCreateOption = canCreateTag(inputValue, suggestions, selectedNames, suggestionsMatchInput);
  const createName = normalizeTagName(inputValue);

  const handleSelect = (name: string) => {
    setInputValue("");
    onSelect(name);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setInputValue("");
      setSearch("");
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        {/* The backend does the matching, so cmdk must not filter the results again. */}
        <Command shouldFilter={false}>
          <CommandInput
            value={inputValue}
            onValueChange={setInputValue}
            placeholder="Search or create a tag"
            maxLength={64}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading tags...
              </div>
            ) : (
              <>
                {suggestions.length === 0 && !showCreateOption && (
                  <CommandEmpty>
                    {createName.length > 0 && createName.length < tagSearchMinLength
                      ? `Type at least ${tagSearchMinLength} characters to search`
                      : "No tags found."}
                  </CommandEmpty>
                )}

                {suggestions.length > 0 && (
                  <CommandGroup heading={search ? "Matching tags" : "Recently used"}>
                    {suggestions.map(tag => (
                      <CommandItem
                        key={tag.id}
                        value={tag.name}
                        onSelect={() => handleSelect(tag.name)}
                        className="cursor-pointer justify-between"
                      >
                        <span className="truncate">{tag.name}</span>
                        {tag.experiment_count !== undefined && (
                          <span className="ml-2 shrink-0 text-xs text-muted-foreground">{tag.experiment_count}</span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {showCreateOption && (
                  <CommandGroup heading="Create">
                    <CommandItem
                      value={`create-${createName}`}
                      onSelect={() => handleSelect(createName)}
                      className="cursor-pointer"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      <span className="truncate">{`Create "${createName}"`}</span>
                    </CommandItem>
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type ExperimentTagCellProps = {
  experiment: Experiment;
  maxVisible?: number;
};

/**
 * Tag list for the experiments table, editable in place so a row can be labelled without
 * opening it. Extra tags collapse into a "+N" badge so a heavily tagged experiment cannot
 * stretch the row; those hidden ones are managed from the detail page.
 *
 * The remove and add controls stay hidden until the row is hovered or focused, so twenty
 * rows of buttons do not drown out the tags themselves.
 */
export function ExperimentTagCell({ experiment, maxVisible = 2 }: ExperimentTagCellProps) {
  const { selectedNames, isMutating, addTag, removeTag } = useExperimentTagEditing(experiment.id, experiment.tags);

  const visible = selectedNames.slice(0, maxVisible);
  const hidden = selectedNames.slice(maxVisible);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map(name => (
        <ExperimentTagBadge
          key={name}
          name={name}
          onRemove={() => removeTag(name)}
          removeDisabled={isMutating}
          removeClassName="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        />
      ))}

      {hidden.length > 0 && (
        <Badge variant="outline" title={hidden.join(", ")}>
          {`+${hidden.length}`}
        </Badge>
      )}

      {selectedNames.length === 0 && <span className="text-muted-foreground">-</span>}

      <ExperimentTagPicker selectedNames={selectedNames} onSelect={addTag}>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
          aria-label={`Add a tag to ${experiment.name}`}
          title="Add tag"
        >
          {isMutating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
        </Button>
      </ExperimentTagPicker>
    </div>
  );
}

type ExperimentTagsEditorProps = {
  experimentId: number;
  tags: ExperimentTag[] | undefined;
  onTagsSaved: (tags: ExperimentTag[]) => void;
};

/**
 * Tag picker for the experiment detail page. Every add and remove saves straight away —
 * tags are metadata, not part of the experiment edit form.
 */
export function ExperimentTagsEditor({ experimentId, tags, onTagsSaved }: ExperimentTagsEditorProps) {
  const { selectedNames, isMutating, addTag, removeTag } = useExperimentTagEditing(experimentId, tags, onTagsSaved);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <TagIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />

      {selectedNames.length === 0 && <span className="text-sm text-muted-foreground">No tags</span>}

      {selectedNames.map(name => (
        <ExperimentTagBadge key={name} name={name} onRemove={() => removeTag(name)} removeDisabled={isMutating} />
      ))}

      <ExperimentTagPicker selectedNames={selectedNames} onSelect={addTag}>
        <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-xs text-muted-foreground">
          {isMutating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Add tag
        </Button>
      </ExperimentTagPicker>
    </div>
  );
}
